import React from 'react';
import 'twin.macro';
import tw from 'twin.macro';
import { BreadcrumbOverflowMenu } from './BreadcrumbOverflowMenu';
import { BreadcrumbHomeButton } from './BreadcrumbHomeButton';
import { BreadcrumbItemWrapper } from './BreadcumbItemWrapper';
import { BreadcrumbItemContent } from './BreadcrumbItemContent';

export interface BreadcrumbItem {
  id: string;
  text: string | React.ReactNode;
  to?: string;
  onClick?: () => void;
  icon?: React.ReactNode | (() => React.ReactNode);
  isActive?: boolean;
  editable?: boolean;
  onEdit?: (value: string) => void;
  editAutoFocus?: boolean;
  hideTextOnMobile?: boolean;
  hideTextOnDesktop?: boolean;
}

interface HomeConfig {
  to?: string;
  onClick?: () => void;
  icon?: React.ReactNode | (() => React.ReactNode);
  text?: string;
  ariaLabel?: string;
  alwaysShowText?: boolean;
}

interface Props {
  className?: string;
  items: BreadcrumbItem[];
  home?: HomeConfig;
  maxVisibleItems?: number;
  overflowMenuItems?: BreadcrumbItem[];
  'data-testid'?: string;
}

function partitionItems(
  items: BreadcrumbItem[],
  maxVisible: number,
): [BreadcrumbItem[], BreadcrumbItem[]] {
  if (items.length <= maxVisible) {
    return [[], items];
  }
  const numHidden = items.length - maxVisible;
  return [items.slice(0, numHidden), items.slice(numHidden)];
}

export function Breadcrumb2(props: Props) {
  const {
    items,
    home,
    maxVisibleItems = Infinity,
    overflowMenuItems = [],
  } = props;
  const [hiddenItems, visibleItems] = partitionItems(items, maxVisibleItems);
  const allOverflowItems = [...overflowMenuItems, ...hiddenItems];

  return (
    <nav
      data-testid={props['data-testid']}
      aria-label="Breadcrumb"
      className={props.className}
      tw="-ml-1 w-full min-w-0"
    >
      <ol role="list" tw="flex items-center space-x-0 min-w-0">
        {(home || allOverflowItems.length > 0) && (
          <li>
            {allOverflowItems.length > 0 && (
              <BreadcrumbOverflowMenu
                home={home}
                overflowItems={allOverflowItems}
              />
            )}
            {allOverflowItems.length === 0 && home && (
              <BreadcrumbHomeButton
                home={home}
                hasVisibleItems={visibleItems.length > 0}
              />
            )}
          </li>
        )}
        {visibleItems.map((item, index) => {
          const isLast = index === visibleItems.length - 1;
          return (
            <BreadcrumbItemWrapper
              key={item.id}
              css={
                isLast
                  ? tw`pr-4 flex-1 min-w-0 min-w-[4rem]`
                  : tw`min-w-0 min-w-[3rem]`
              }
            >
              <div
                css={[
                  tw`items-center min-w-0`,
                  !isLast && tw`max-w-[8rem] lg:max-w-[10rem] xl:max-w-[15rem]`,
                  isLast && tw`flex flex-1`,
                ]}
              >
                <BreadcrumbItemContent item={item} isLast={isLast} />
              </div>
            </BreadcrumbItemWrapper>
          );
        })}
      </ol>
    </nav>
  );
}
